/**
 * Full Send Theme JavaScript
 * Custom functionality for Full Send e-commerce site
 */

(function() {
  'use strict';

  // ===========================================
  // AGE GATE FUNCTIONALITY
  // ===========================================

  const AgeGate = {
    COOKIE_NAME: 'fullsend_age_verified',
    modal: null,
    config: null,

    /**
     * Initialize age gate functionality
     */
    init: function() {
      // B2B customers are pre-verified at signup — skip the local age gate.
      if (window.fullsendB2B && window.fullsendB2B.isB2B) return;

      this.config = window.fullsendConfig || {};
      this.modal = document.getElementById('age-gate-modal');

      if (!this.config.ageGateEnabled || !this.modal) {
        return;
      }

      this.bindEvents();
      this.checkAndShowModal();
    },

    /**
     * Bind click events for modal buttons
     */
    bindEvents: function() {
      const confirmBtn = this.modal.querySelector('[data-age-gate-confirm]');
      const denyBtn = this.modal.querySelector('[data-age-gate-deny]');

      if (confirmBtn) {
        confirmBtn.addEventListener('click', this.handleConfirm.bind(this));
      }

      if (denyBtn) {
        denyBtn.addEventListener('click', this.handleDeny.bind(this));
      }

      // Email capture: Enter submits; copy + start-shopping buttons.
      const emailInput = this.modal.querySelector('[data-age-gate-email]');
      if (emailInput) {
        emailInput.addEventListener('keydown', function(e) {
          if (e.key === 'Enter') { e.preventDefault(); this.handleConfirm(); }
        }.bind(this));
        emailInput.addEventListener('input', this.hideEmailError.bind(this));
      }

      const copyBtn = this.modal.querySelector('[data-age-gate-copy]');
      if (copyBtn) {
        copyBtn.addEventListener('click', this.handleCopy.bind(this));
      }

      const startBtn = this.modal.querySelector('[data-age-gate-start]');
      if (startBtn) {
        startBtn.addEventListener('click', this.hideModal.bind(this));
      }

      // Prevent closing modal by clicking backdrop
      const backdrop = this.modal.querySelector('.age-gate-modal__backdrop');
      if (backdrop) {
        backdrop.addEventListener('click', function(e) {
          e.stopPropagation();
        });
      }

      // Prevent escape key from closing
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && !this.modal.hidden) {
          e.preventDefault();
        }
      }.bind(this));
    },

    /**
     * Check if modal should be shown and show it if necessary
     */
    checkAndShowModal: function() {
      // Check if we're on a nicotine product page
      if (!this.isNicotineProductPage()) {
        return;
      }

      // Check if user has already verified
      if (this.isVerified()) {
        return;
      }

      // Show the modal
      this.showModal();
    },

    /**
     * Determine if age gate should be shown on current page
     * Since nicotine products are featured site-wide, show on all pages
     */
    isNicotineProductPage: function() {
      // Show age gate on all pages since nicotine products are displayed throughout the site
      return true;
    },

    /**
     * Check if user has verified their age (cookie exists)
     */
    isVerified: function() {
      return this.getCookie(this.COOKIE_NAME) === 'true';
    },

    /**
     * Show the age gate modal
     */
    showModal: function() {
      this.modal.hidden = false;
      document.body.style.overflow = 'hidden';

      // Focus on confirm button for accessibility
      const confirmBtn = this.modal.querySelector('[data-age-gate-confirm]');
      if (confirmBtn) {
        setTimeout(function() {
          confirmBtn.focus();
        }, 100);
      }

      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('ageGate:shown'));
    },

    /**
     * Hide the age gate modal
     */
    hideModal: function() {
      this.modal.hidden = true;
      document.body.style.overflow = '';

      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('ageGate:hidden'));
    },

    /**
     * Whether the email-capture UI is present (feature enabled + code set).
     */
    hasEmailCapture: function() {
      return !!this.modal.querySelector('[data-age-gate-capture]');
    },

    /**
     * Verify age and close the modal (no-email path). Original behavior.
     */
    verifyAndClose: function() {
      const days = this.config.ageGateCookieDays || 1;
      this.setCookie(this.COOKIE_NAME, 'true', days);
      this.hideModal();
      document.dispatchEvent(new CustomEvent('ageGate:confirmed'));
    },

    /**
     * Handle confirm button click (user is 21+).
     * With email capture on: empty = skip+close, invalid = inline error,
     * valid = verify + best-effort Klaviyo subscribe + reveal discount.
     */
    handleConfirm: function() {
      if (!this.hasEmailCapture()) {
        this.verifyAndClose();
        return;
      }

      const input = this.modal.querySelector('[data-age-gate-email]');
      const email = input ? input.value.trim() : '';

      // Empty → skip path: verify + close, no discount, no Klaviyo call.
      if (!email) {
        this.verifyAndClose();
        return;
      }

      // Invalid → inline error, stay open, do not verify yet.
      const emailRe = (window.fullsendKlaviyo && window.fullsendKlaviyo.EMAIL_RE) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(email)) {
        this.showEmailError('Please enter a valid email.');
        if (input) input.focus();
        return;
      }

      // Valid → verify now, then best-effort subscribe, then reveal.
      this.hideEmailError();
      this.setCookie(this.COOKIE_NAME, 'true', this.config.ageGateCookieDays || 1);
      document.dispatchEvent(new CustomEvent('ageGate:confirmed'));

      const confirmBtn = this.modal.querySelector('[data-age-gate-confirm]');
      if (confirmBtn) { confirmBtn.disabled = true; confirmBtn.classList.add('is-loading'); }

      const self = this;
      const finish = function() { self.revealDiscount(); };

      if (window.fullsendKlaviyo && typeof window.fullsendKlaviyo.subscribe === 'function') {
        window.fullsendKlaviyo.subscribe({ email: email, source: 'Age Gate 21+ Popup' })
          .then(function() {
            document.dispatchEvent(new CustomEvent('ageGate:subscribed', { detail: { email: email } }));
          })
          .catch(function(err) {
            // Optimistic reveal: still show the code; log the failure reason.
            console.warn('[age-gate] Klaviyo subscribe failed (showing discount anyway):', err && err.message);
          })
          .then(finish);
      } else {
        console.warn('[age-gate] window.fullsendKlaviyo.subscribe unavailable; showing discount without capture.');
        finish();
      }
    },

    /**
     * Show the inline email validation/error message.
     */
    showEmailError: function(msg) {
      const el = this.modal.querySelector('[data-age-gate-email-error]');
      if (el) { el.textContent = msg; el.hidden = false; }
    },

    /**
     * Clear the inline email error.
     */
    hideEmailError: function() {
      const el = this.modal.querySelector('[data-age-gate-email-error]');
      if (el) { el.hidden = true; el.textContent = ''; }
    },

    /**
     * Swap the modal body to the success / discount-reveal state.
     */
    revealDiscount: function() {
      const body = this.modal.querySelector('.fs-gate__body');
      const success = this.modal.querySelector('[data-age-gate-success]');
      if (body) body.classList.add('fs-gate__body--success');
      if (success) {
        success.hidden = false;
        const startBtn = success.querySelector('[data-age-gate-start]');
        if (startBtn) setTimeout(function() { startBtn.focus(); }, 50);
      }
    },

    /**
     * Copy the revealed discount code to the clipboard.
     */
    handleCopy: function() {
      const codeEl = this.modal.querySelector('[data-age-gate-code]');
      const btn = this.modal.querySelector('[data-age-gate-copy]');
      if (!codeEl) return;
      const code = (codeEl.textContent || '').trim();
      const done = function() {
        if (!btn) return;
        const prev = btn.getAttribute('data-label') || 'Copy';
        btn.textContent = 'Copied';
        btn.classList.add('is-copied');
        setTimeout(function() { btn.textContent = prev; btn.classList.remove('is-copied'); }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done).catch(done);
      } else {
        try {
          const ta = document.createElement('textarea');
          ta.value = code; document.body.appendChild(ta); ta.select();
          document.execCommand('copy'); document.body.removeChild(ta);
        } catch (e) {}
        done();
      }
    },

    /**
     * Handle deny button click (user is under 21)
     */
    handleDeny: function() {
      const denyBtn = this.modal.querySelector('[data-age-gate-deny]');
      const redirectUrl = denyBtn ? denyBtn.dataset.redirectUrl : '/';

      // Dispatch custom event before redirect
      document.dispatchEvent(new CustomEvent('ageGate:denied'));

      // Redirect to specified URL or homepage
      window.location.href = redirectUrl || '/';
    },

    /**
     * Set a cookie
     */
    setCookie: function(name, value, days) {
      let expires = '';
      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = '; expires=' + date.toUTCString();
      }
      document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
    },

    /**
     * Get a cookie value
     */
    getCookie: function(name) {
      const nameEQ = name + '=';
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i];
        while (cookie.charAt(0) === ' ') {
          cookie = cookie.substring(1);
        }
        if (cookie.indexOf(nameEQ) === 0) {
          return decodeURIComponent(cookie.substring(nameEQ.length));
        }
      }
      return null;
    },

    /**
     * Clear the verification cookie (for testing)
     */
    clearVerification: function() {
      this.setCookie(this.COOKIE_NAME, '', -1);
    }
  };

  // ===========================================
  // PRODUCT 21+ BADGE
  // ===========================================

  const ProductBadge = {
    /**
     * Initialize 21+ badges on product cards
     */
    init: function() {
      const nicotineProducts = document.querySelectorAll('[data-product-nicotine="true"]');
      nicotineProducts.forEach(function(product) {
        this.addBadge(product);
      }.bind(this));
    },

    /**
     * Add 21+ badge to a product element
     */
    addBadge: function(element) {
      // Skip main product section - it has its own nicotine warning banner
      if (element.classList.contains('main-product')) {
        return;
      }

      // Check if badge already exists
      if (element.querySelector('.product-badge-21')) {
        return;
      }

      const badge = document.createElement('span');
      badge.className = 'product-badge-21';
      badge.textContent = '21+';
      badge.setAttribute('aria-label', 'Must be 21 or older to purchase');

      // Find image container or add to element directly
      const imageContainer = element.querySelector('.product-card__image-wrapper, .card__media');
      if (imageContainer) {
        imageContainer.style.position = 'relative';
        imageContainer.appendChild(badge);
      } else {
        element.style.position = 'relative';
        element.appendChild(badge);
      }
    }
  };

  // ===========================================
  // UTILITY FUNCTIONS
  // ===========================================

  const Utils = {
    /**
     * Debounce function calls
     */
    debounce: function(func, wait) {
      let timeout;
      return function executedFunction() {
        const context = this;
        const args = arguments;
        const later = function() {
          timeout = null;
          func.apply(context, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    /**
     * Format currency
     */
    formatMoney: function(cents, format) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }
      let value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || '${{amount}}';

      function defaultOption(opt, def) {
        return (typeof opt === 'undefined' ? def : opt);
      }

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultOption(precision, 2);
        thousands = defaultOption(thousands, ',');
        decimal = defaultOption(decimal, '.');

        if (isNaN(number) || number == null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const cents = parts[1] ? (decimal + parts[1]) : '';

        return dollars + cents;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
      }

      return formatString.replace(placeholderRegex, value);
    }
  };

  // ===========================================
  // INITIALIZATION
  // ===========================================

  function init() {
    AgeGate.init();
    ProductBadge.init();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose modules globally for external access (merge with existing)
  window.FullSend = window.FullSend || {};
  Object.assign(window.FullSend, {
    AgeGate: AgeGate,
    ProductBadge: ProductBadge,
    Utils: Utils
  });

})();
